import { ComponentConfig, FieldLabel } from '@measured/puck';
import React, { useEffect, useState } from 'react';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
  PaddingType,
} from '../../puck/reusable-props/padding';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { MarginProps, marginFields } from '../../puck/reusable-props/NewMargin';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import { AxiosInstance } from 'axios';
import {
  getBorderProps,
  getcurrentDeviceType,
  getNewMarginStyles,
  getPaddingStyles,
  getSpanPercentage,
} from '../../utils/helper';
import WithAccordion from '../customComponents/CustomAccordion';
import {
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { ImageProps, imageProps } from '../../puck/reusable-props/image-props';
import ProductModal, {
  defaultProduct,
  IProductModalProps,
} from './AddProductModal';
import PreviewProduct from './PreviewProduct';
import InputField from '../../../components/common/input-field';
import { CustomizeProps } from '../../interface/commonInterface';
import TextCustomInput from '../../puck/reusable-props/TextCustomInput';
import { setFunnelishProducts } from '../../redux/slices/cartSlice';
import { toolTipInfo } from '../../utils/constants';
import PaddingComponent from '../customComponents/PaddingComponent';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import WidthComponent, { IWidthComponentProps } from '../blocks/widthComponent';
import QuilHelper from '../../puck/reusable-props/quilHelper';
import { InfoIcon } from 'lucide-react';
import { boxShadows } from '../../puck/reusable-props/options';

type textFields = {
  text: string;
  showText: boolean;
};

type tagstyle = {
  isBlink: string;
  borderTopLeft: string;
  borderTopRight: string;
  borderBottomLeft: string;
  borderBottomRight: string;
  padding: PaddingType;
  backgroundColor: string;
};
export type PredefinedProps = MarginProps &
  ImageProps &
  PaddingProps & {
    background: {
      color: string;
      isInherit: boolean;
    };
    configurations: {
      configId: number;
      products: IProductModalProps[];
    };
    productTitle: CustomizeProps;
    width: string;
    tagWidth: IWidthComponentProps;
    tagPosition: string;
    border: IBorderValueProps;
    borderForProducts: IBorderValueProps;
    radioColor: string;
    radioSize: string;
    leftHeaderNew: textFields;
    rightHeaderNew: textFields;
    showDivider: boolean;
    tagStyles: tagstyle;
    leftTagStyles: tagstyle;
    selectionStyle: {
      isToggle: boolean;
      bannerStyle: {
        backgroundColor: string;
        border: IBorderValueProps;
        shadow: string;
        titleColor: string;
      };
    };
  };

export interface PredefinedProductComponentProps extends ReduxSelectorProps {
  baseService: AxiosInstance;
}

export const Predefined = ({
  useAppSelector,
  baseService,
  useAppDispatch,
}: PredefinedProductComponentProps &
  ReduxDispatchProps): ComponentConfig<PredefinedProps> => {
  return {
    label: (
      <CustomTooltip title="PreDefined">
        <span>Predefined Products</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      configurations: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <ProductModal
            value={value}
            onChange={onChange}
            baseService={baseService}
          />
        ),
      },
      leftHeaderNew: {
        type: 'custom',
        label: 'Left Header',
        render: ({ value, onChange }) => (
          <WithAccordion title="Left Header">
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold">Show Left Header</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title="This property will define whether to show the left header in the header options of the component."
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-4 w-4" />
                </Tooltip>
                <Switch
                  onChange={e => {
                    onChange({
                      ...value,
                      showText: e.target.checked,
                    });
                  }}
                  checked={value?.showText}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>

            {value?.showText && (
              <QuilHelper
                value={value?.text}
                onChange={text => onChange({ ...value, text })}
                id={4}
              />
            )}
          </WithAccordion>
        ),
      },
      rightHeaderNew: {
        type: 'custom',
        label: 'Right Header',
        render: ({ value, onChange }) => (
          <WithAccordion title="Right Header">
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold">Show Right Header</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title="This property will define whether to show the right header in the header options of the component."
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-4 w-4" />
                </Tooltip>
                <Switch
                  onChange={e => {
                    onChange({
                      ...value,
                      showText: e.target.checked,
                    });
                  }}
                  checked={value?.showText}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>

            {value?.showText && (
              <QuilHelper
                value={value?.text}
                onChange={text => onChange({ ...value, text })}
                id={5}
              />
            )}
          </WithAccordion>
        ),
      },
      showDivider: {
        type: 'custom',
        render: ({ onChange, value }) => {
          return (
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">Show Divider</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title={
                    'This property will define whether to show the divider in the header options of the component.'
                  }
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-4 w-4" />
                </Tooltip>
                <Switch
                  onChange={e => {
                    onChange(e.target.checked);
                  }}
                  checked={value}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>
          );
        },
      },
      ...imageProps([]),
      productTitle: {
        type: 'custom',
        label: 'Product Title',
        render: ({ value, onChange, field }) => (
          <TextCustomInput
            value={value}
            onChange={onChange}
            title={field?.label}
            isText={false}
          />
        ),
      },
      width: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <InputField
            value={value}
            label="Product Width"
            onChange={e => {
              const newValue = getSpanPercentage(e);
              if (newValue !== null) {
                onChange(newValue);
              }
            }}
          />
        ),
      },
      borderForProducts: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={onChange}
            title="Border For Products"
          />
        ),
      },
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      selectionStyle: {
        type: 'custom',
        label: 'Selection Style',
        render: ({ value, onChange }) => (
          <WithAccordion title="Selection Style">
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">Toggle Selection Style</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title="If enabled, Toggle selection style will be Applied"
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-4 w-4" />
                </Tooltip>
                <Switch
                  onChange={e => {
                    onChange({
                      ...value,
                      isToggle: e.target.checked,
                    });
                  }}
                  checked={value?.isToggle || false}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>

            {!value?.isToggle && (
              <WithAccordion title="Selected Banner Style">
                <ColorPicker
                  value={value?.bannerStyle?.backgroundColor || '#ffffff'}
                  onChange={color =>
                    onChange({
                      ...value,
                      bannerStyle: {
                        ...value?.bannerStyle,
                        backgroundColor: color,
                      },
                    })
                  }
                  label={'Background Color'}
                  tooltip="Background Color"
                />
                <Border
                  value={value?.bannerStyle?.border}
                  onChange={borderValue =>
                    onChange({
                      ...value,
                      bannerStyle: {
                        ...value?.bannerStyle,
                        border: borderValue,
                      },
                    })
                  }
                  title="Banner Border"
                />
                <FieldLabel label="Shadow">
                  <select
                    value={value?.bannerStyle?.shadow || ''}
                    onChange={e =>
                      onChange({
                        ...value,
                        bannerStyle: {
                          ...value?.bannerStyle,
                          shadow: e.target.value,
                        },
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md">
                    {boxShadows.map((shadowOption, index) => (
                      <option key={index} value={shadowOption?.value}>
                        {shadowOption?.label}
                      </option>
                    ))}
                  </select>
                </FieldLabel>

                <ColorPicker
                  value={value?.bannerStyle?.titleColor || '#000000'}
                  onChange={color =>
                    onChange({
                      ...value,
                      bannerStyle: {
                        ...value?.bannerStyle,
                        titleColor: color,
                      },
                    })
                  }
                  label="Title Color"
                  tooltip="Text Color"
                />
              </WithAccordion>
            )}
          </WithAccordion>
        ),
      },
      radioColor: {
        type: 'custom',
        label: 'Selected radio color',
        render: ({ value, onChange, field }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={field.label}
          />
        ),
      },
      radioSize: {
        type: 'custom',
        label: 'Radio Size',
        render: ({ value, onChange }) => (
          <>
            <label>Radio Size</label>
            <input
              type="Number"
              value={value || ''}
              onChange={newValue => {
                onChange(newValue.target.value);
              }}
            />
          </>
        ),
      },

      tagPosition: {
        type: 'select',
        label: 'Tag Position',
        options: [
          { label: 'Left', value: 'top' },
          { label: 'Image Right', value: 'right' },
          { label: 'Center', value: 'top-center' },
          { label: 'Right', value: 'top-right' },
        ],
      },
      tagWidth: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <WidthComponent onChange={onChange} value={value} title="Tag Width" />
        ),
      },
      tagStyles: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <WithAccordion title="Tag Customization">
            <ColorPicker
              value={value?.backgroundColor || '#000000'}
              onChange={color =>
                onChange({ ...(value || {}), backgroundColor: color })
              }
              label="Background Color"
              tooltip="Background Color"
            />
            <FieldLabel
              label={
                (
                  <CustomTooltip title="Border Radius">
                    <span>Border Radius</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }>
              <div className="grid grid-cols-[1fr_1fr] gap-2 border-t-1">
                <InputField
                  label="Top-Left"
                  value={value?.borderTopLeft}
                  className="h-[42px]"
                  onChange={e => {
                    const newValue = e.replace(/[^0-9%]/g, '');
                    onChange({
                      ...value,
                      borderTopLeft: newValue,
                    });
                  }}
                />
                <InputField
                  className="h-[42px]"
                  value={value?.borderTopRight}
                  onChange={e => {
                    const newValue = e.replace(/[^0-9%]/g, '');
                    onChange({
                      ...value,
                      borderTopRight: newValue,
                    });
                  }}
                  label="Top-Right"
                />
                <InputField
                  className="h-[42px]"
                  value={value?.borderBottomLeft}
                  onChange={e => {
                    const newValue = e.replace(/[^0-9%]/g, '');
                    onChange({
                      ...value,
                      borderBottomLeft: newValue,
                    });
                  }}
                  label="Bottom-Left"
                />
                <InputField
                  className="h-[42px]"
                  value={value?.borderBottomRight}
                  onChange={e => {
                    const newValue = e.replace(/[^0-9%]/g, '');
                    onChange({
                      ...value,
                      borderBottomRight: newValue,
                    });
                  }}
                  label="Bottom-Right"
                />
              </div>
            </FieldLabel>
            <FieldLabel
              label={
                (
                  <CustomTooltip
                    title={'Pulse_Animation' as keyof typeof toolTipInfo}>
                    <span>Pulse Animation</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }
              el="div"
              className="w-full">
              <div className="w-full p-2">
                <ToggleButtonGroup
                  value={value?.isBlink}
                  exclusive
                  className="w-full"
                  onChange={(_e, selectValue) =>
                    onChange({ ...value, isBlink: selectValue })
                  }>
                  {[
                    {
                      label: 'Yes',
                      value: 'yes',
                    },
                    {
                      label: 'No',
                      value: 'no',
                    },
                  ].map((option, index: number) => (
                    <ToggleButton
                      key={index}
                      size="small"
                      value={option.value}
                      disableRipple
                      aria-label={option.value}
                      className="flex-1">
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </div>
            </FieldLabel>
            <PaddingComponent
              value={value?.padding}
              onChange={newPadding => {
                onChange({
                  ...value,
                  padding: {
                    ...value.padding,
                    ...newPadding,
                  },
                });
              }}
              title="Padding"
            />
          </WithAccordion>
        ),
      },
      leftTagStyles: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <WithAccordion title="Left Vertical Tag Customization">
            <ColorPicker
              value={value?.backgroundColor || '#000000'}
              onChange={color =>
                onChange({ ...(value || {}), backgroundColor: color })
              }
              label="Background Color"
              tooltip="Background Color"
            />
            <FieldLabel
              label={
                (
                  <CustomTooltip title="Border Radius">
                    <span>Border Radius</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }>
              <div className="grid grid-cols-[1fr_1fr] gap-2 border-t-1">
                <InputField
                  label="Top-Left"
                  value={value?.borderTopLeft}
                  className="h-[42px]"
                  onChange={e => {
                    const newValue = e.replace(/[^0-9%]/g, '');
                    onChange({
                      ...value,
                      borderTopLeft: newValue,
                    });
                  }}
                />
                <InputField
                  className="h-[42px]"
                  value={value?.borderTopRight}
                  onChange={e => {
                    const newValue = e.replace(/[^0-9%]/g, '');
                    onChange({
                      ...value,
                      borderTopRight: newValue,
                    });
                  }}
                  label="Top-Right"
                />
                <InputField
                  className="h-[42px]"
                  value={value?.borderBottomLeft}
                  onChange={e => {
                    const newValue = e.replace(/[^0-9%]/g, '');
                    onChange({
                      ...value,
                      borderBottomLeft: newValue,
                    });
                  }}
                  label="Bottom-Left"
                />
                <InputField
                  className="h-[42px]"
                  value={value?.borderBottomRight}
                  onChange={e => {
                    const newValue = e.replace(/[^0-9%]/g, '');
                    onChange({
                      ...value,
                      borderBottomRight: newValue,
                    });
                  }}
                  label="Bottom-Right"
                />
              </div>
            </FieldLabel>
            <PaddingComponent
              value={value?.padding}
              onChange={newPadding => {
                onChange({
                  ...value,
                  padding: {
                    ...value.padding,
                    ...newPadding,
                  },
                });
              }}
              title="Padding"
            />
          </WithAccordion>
        ),
      },
      ...backgroungFields,
      ...paddingFields,
      ...marginFields,
    },
    defaultProps: {
      borderForProducts: {
        deviceType: 'desktop',
        borderBottomLeft: {
          desktop: '10',
          tablet: '10',
          mobile: '10',
        },
        borderBottomRight: {
          desktop: '10',
          tablet: '10',
          mobile: '10',
        },
        borderTopLeft: {
          desktop: '10',
          tablet: '10',
          mobile: '10',
        },
        borderTopRight: {
          desktop: '10',
          tablet: '10',
          mobile: '10',
        },
        borderTopWidth: {
          desktop: 1,
          tablet: 1,
          mobile: 1,
        },
        borderRightWidth: {
          desktop: 1,
          tablet: 1,
          mobile: 1,
        },
        borderBottomWidth: {
          desktop: 1,
          tablet: 1,
          mobile: 1,
        },
        borderLeftWidth: {
          desktop: 1,
          tablet: 1,
          mobile: 1,
        },
        borderWidth: {
          desktop: 1,
          tablet: 1,
          mobile: 1,
        },
        borderStyle: {
          desktop: 'border-solid',
          tablet: 'border-solid',
          mobile: 'border-solid',
        },
        borderColor: {
          desktop: '#a3a3a3',
          tablet: '#a3a3a3',
          mobile: '#a3a3a3',
        },
      },
      radioColor: '#2563EB',
      radioSize: '20',
      margin: {
        deviceType: 'desktop',
        marginBottom: {
          desktop: '4',
          mobile: '2',
          tablet: '4',
        },
        marginLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginTop: {
          desktop: '4',
          mobile: '2',
          tablet: '4',
        },
      },
      tagWidth: {
        deviceType: 'desktop',
        width: {
          desktop: '100%',
          mobile: '100%',
          tablet: '100%',
        },
      },
      tagPosition: 'top',
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '8',
          mobile: '6',
          tablet: '8',
        },
        paddingLeft: {
          desktop: '8',
          mobile: '6',
          tablet: '8',
        },
        paddingRight: {
          desktop: '8',
          mobile: '6',
          tablet: '8',
        },
        paddingTop: {
          desktop: '8',
          mobile: '6',
          tablet: '8',
        },
      },
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      ...borderProps,
      leftHeaderNew: {
        showText: true,
        text: '<p>Product</p>',
      },
      rightHeaderNew: {
        showText: true,
        text: '<p>Price</p>',
      },
      showDivider: true,
      selectionStyle: {
        isToggle: true,
        bannerStyle: {
          backgroundColor: '#ffffff',
          border: { ...borderProps.border },
          shadow: 'none',
          titleColor: '#000000',
        },
      },
      image: {
        height: {
          desktop: '60',
          mobile: '60',
          tablet: '60',
        },
        maxWidth: {
          desktop: '60',
          mobile: '60',
          tablet: '60',
        },
        deviceType: 'desktop',
      },
      configurations: {
        configId: 0,
        products: [defaultProduct],
      },
      width: '100%',
      productTitle: {
        weight: {
          mobile: 'font-semibold',
          tablet: 'font-semibold',
          desktop: 'font-semibold',
        },
        deviceType: 'desktop',
        text: '',
        textColor: {
          mobile: '#000000',
          tablet: '#000000',
          desktop: '#000000',
        },
        hoverColor: {
          mobile: '#000000',
          tablet: '#000000',
          desktop: '#000000',
        },
        fontSize: {
          mobile: 'text-sm',
          tablet: 'text-sm',
          desktop: 'text-sm',
        },
      },
      tagStyles: {
        isBlink: 'no',
        borderTopLeft: '0',
        borderTopRight: '0',
        borderBottomLeft: '0',
        borderBottomRight: '0',
        ...paddingProps,
        backgroundColor: '#FFFFFF',
      },
      leftTagStyles: {
        isBlink: 'no',
        borderTopLeft: '0',
        borderTopRight: '0',
        borderBottomLeft: '0',
        borderBottomRight: '0',
        ...paddingProps,
        backgroundColor: '#FFFFFF',
      },
    },
    render: ({
      margin,
      padding,
      editMode,
      background,
      leftHeaderNew,
      rightHeaderNew,
      showDivider,
      border,
      image,
      configurations,
      width,
      productTitle,
      tagStyles,
      tagWidth,
      borderForProducts,
      radioColor,
      radioSize,
      tagPosition,
      selectionStyle,
      leftTagStyles,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const dispatch = useAppDispatch();
      const { cartData } = useAppSelector(state => state.cart);
      const { storeData } = useAppSelector(state => state.store);
      const { pageId } = useAppSelector(state => state.checkout);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getNewMarginStyles(margin, currentDeviceType);
      const paddingStyles = getPaddingStyles(padding, currentDeviceType);
      const [selectedProduct, setSelectedProduct] = useState<number | null>(
        null,
      );
      let maxContainerWidth: string;
      if (tagWidth?.width?.[currentDeviceType]?.includes('%')) {
        maxContainerWidth = tagWidth?.width?.[currentDeviceType];
      } else if (
        tagWidth?.width?.[currentDeviceType] &&
        tagWidth?.width?.[currentDeviceType] !== 'none'
      ) {
        maxContainerWidth = `${tagWidth?.width?.[currentDeviceType]}px`;
      } else {
        maxContainerWidth = '100%';
      }

      useEffect(() => {
        setSelectedProduct(
          configurations?.products?.find(item => item.defaultProduct)
            ?.productDetails.id || null,
        );
        dispatch(setFunnelishProducts(configurations?.products || []));
      }, [configurations?.products]);

      return (
        <div
          className={`${border?.borderStyle?.[currentDeviceType]}`}
          style={{
            ...marginStyles,
            ...paddingStyles,
            ...getBorderProps(border, currentDeviceType),
            backgroundColor: background?.isInherit
              ? 'inherit'
              : background?.color,
            width: isNaN(+width) ? width : width + 'px',
          }}>
          {(leftHeaderNew?.showText || rightHeaderNew?.showText) && (
            <div className="flex justify-between text-sm font-medium mb-3">
              <div>
                {leftHeaderNew?.showText ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: leftHeaderNew?.text }}
                  />
                ) : (
                  <div></div>
                )}
              </div>
              <div>
                {rightHeaderNew?.showText ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: rightHeaderNew?.text }}
                  />
                ) : (
                  <div></div>
                )}
              </div>
            </div>
          )}

          {showDivider && <hr className="border-t-1 border-gray-200 mb-2" />}
          <div className="grid grid-cols-1 gap-3">
            <PreviewProduct
              pageId={pageId}
              productTitle={productTitle}
              maxContainerWidth={maxContainerWidth}
              data={configurations.products}
              cartData={cartData}
              tagPosition={tagPosition}
              storeData={storeData}
              dispatch={dispatch}
              currentDeviceType={currentDeviceType}
              image={image}
              tagStyles={tagStyles}
              leftTagStyles={leftTagStyles}
              selectedProduct={selectedProduct || 0}
              setSelectedProduct={setSelectedProduct}
              borderForProducts={borderForProducts}
              radioColor={radioColor}
              radioSize={radioSize}
              selectionStyle={selectionStyle}
              editMode={editMode}
            />
          </div>
        </div>
      );
    },
  };
};
